import { useState } from 'react';
import { AlertTriangle, CheckCircle, X } from 'lucide-react';

// Custom confirmation modal to replace window.confirm()
// Supports both old props (isOpen, onClose) and new props (open, onCancel)
const ConfirmModal = ({ 
  isOpen, 
  open, 
  onClose, 
  onCancel, 
  onConfirm, 
  title, 
  message, 
  type = 'warning',
  variant 
}) => {
  // Support both prop naming conventions
  const isVisible = open ?? isOpen;
  const handleClose = onCancel ?? onClose;
  const modalType = variant ?? type;

  if (!isVisible) return null;

  const colors = {
    warning: { bg: '#fef3c7', border: '#f59e0b', icon: '#f59e0b', button: '#f59e0b' },
    danger: { bg: '#fee2e2', border: '#ef4444', icon: '#ef4444', button: '#ef4444' },
    info: { bg: '#e0f2ff', border: '#0073ea', icon: '#0073ea', button: '#0073ea' },
    success: { bg: '#dcfce7', border: '#16a34a', icon: '#16a34a', button: '#16a34a' }
  };

  const color = colors[modalType] || colors.warning;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        maxWidth: '450px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          background: color.bg,
          borderBottom: `2px solid ${color.border}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {type === 'success' ? (
              <CheckCircle size={28} color={color.icon} />
            ) : (
              <AlertTriangle size={28} color={color.icon} />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.1rem', 
              fontWeight: 600, 
              color: '#1f2937',
              marginBottom: '0.5rem'
            }}>
              {title}
            </h3>
            <p style={{ 
              margin: 0, 
              fontSize: '0.9rem', 
              color: '#4b5563',
              lineHeight: 1.5,
              whiteSpace: 'pre-line'
            }}>
              {message}
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              color: '#9ca3af'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Actions */}
        <div style={{
          padding: '1rem 1.5rem',
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleClose}
            data-testid="confirm-modal-cancel"
            style={{
              padding: '0.75rem 1.5rem',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (onConfirm) onConfirm();
            }}
            data-testid="confirm-modal-confirm"
            style={{
              padding: '0.75rem 1.5rem',
              background: color.button,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook to use the confirmation modal
export const useConfirm = () => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });

  const confirm = ({ title, message, type = 'warning' }) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        title,
        message,
        type,
        onConfirm: () => resolve(true)
      });
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const ConfirmDialog = () => (
    <ConfirmModal
      isOpen={modalState.isOpen}
      onClose={closeModal}
      onConfirm={modalState.onConfirm}
      title={modalState.title}
      message={modalState.message}
      type={modalState.type}
    />
  );

  return { confirm, ConfirmDialog };
};

export default ConfirmModal;
